import * as React from 'react';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import { Button, Radio } from 'antd';
import { Link } from 'react-router-dom';

import { ObjectUtils } from 'src/core/utils/object';
import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { IdentityService } from 'src/core/services/identity.service';
import { DateView } from 'src/core/ui/date.view';
import { UserEditorController } from 'src/app/general/users/parts/user-editor.controller';
import { ClientEditorController } from 'src/app/crm/clients/parts/client-editor.controller'
import { StatusTexts } from "src/app/shared/status-texts";
import { MarketEditorCheckListController } from 'src/app/general/market/parts/market-editor-checkList.controller';
import { BrandEditorCheckListController } from 'src/app/general/brands/parts/brand-editor-checkList.controller';

const RadioGroup = Radio.Group;

interface QuoteListViewProps extends ComponentViewProps<DataSet<QuoteSummary>> {
	onItemDoubleClicked?: (item: QuoteSummary) => void;
	hiddenColumns?: string[];
	readonly?: boolean;
}

@observer
export class QuoteListView extends React.Component<QuoteListViewProps, {}> {
	@resolve
	private identityService: IdentityService;

	private permissions = ['Quote', 'Quote_Write', 'Quote_Write_Own'];

	public render() {
		const { model, hiddenColumns } = this.props;
		const radioStyle = {
			display: 'block',
			height: '30px',
			lineHeight: '30px',
		};
		const columns: Column[] = [
			{
				key: 'quoteNumber',
				title: 'Reference',
				dataIndex: 'quoteNumber',
				sortable: true,
				render: (text, record: QuoteSummary) => {
					let txt = record.status == 0 ? `Quote ${record.id}` : text;
					return <Link to={`/bookings/quotes/${record.id}`}>{txt}</Link>
				}
			},
			{
				key: 'clientId',
				title: 'Client',
				dataIndex: 'clientId',
				sortable: true,
				filter: {
					field: 'clientId',
					type: 'foreignKey',
					render: (value, onChange) => (
						<ClientEditorController
							label={null}
							mode='edit'
							value={value}
							onChanged={(value: ClientSummary) => { onChange(value ? value.id : null) }}
							setBusy={this.props.setBusy}
						/>
					)
				},
				render: (text, record: QuoteSummary) => <Link to={`/crm/clients/${record.clientId}`}>{record.clientDisplayName}</Link>,
			},
			{
				key: 'statusText',
				title: 'Status',
				dataIndex: 'statusText',
				sortable: true,
				filter: {
					field: 'status',
					type: 'number',
					render: (value, onChange) => (
						<RadioGroup
							onChange={(e) => {
								if (e.target.value == -1) {
									onChange(null);
								} else {
									onChange(e.target.value);
								}
							}}
							value={value != null ? value : -1}
						>
							<Radio style={radioStyle} value={-1}>All</Radio>
							<Radio style={radioStyle} value={0}>{StatusTexts.getQuoteStatusFromNumber(0)}</Radio>
							<Radio style={radioStyle} value={1}>{StatusTexts.getQuoteStatusFromNumber(1)}</Radio>
							<Radio style={radioStyle} value={2}>{StatusTexts.getQuoteStatusFromNumber(2)}</Radio>
							<Radio style={radioStyle} value={3}>{StatusTexts.getQuoteStatusFromNumber(3)}</Radio>
							<Radio style={radioStyle} value={4}>{StatusTexts.getQuoteStatusFromNumber(4)}</Radio>
						</RadioGroup>
					)
				},
				render: (text: string, record: QuoteSummary) => {
					let status;
					let icon;
					let style: any = {};
					switch (record.status) {
						case 1:
							status = 'primary';
							style.backgroundColor = '#ffffcd';
							style.borderColor = 'black';
							style.color = 'black';
							break;
						case 2:
							status = 'primary';
							style.backgroundColor = '#28a745';
							style.borderColor = '#28a745';
							break;
						case 3:
							status = 'primary';
							icon = "check";
							break;
						case 4:
							status = 'danger';
							break;
					}
					return <Button type={status} style={style} icon={icon} size="small">{text}</Button>;
				}
			},
			{
				key: 'marketName',
				title: 'Market',
				dataIndex: 'marketId',
				sortable: true,
				filter: {
					field: 'marketId',
					type: 'foreignKey',
					render: (value, onChange) => (
						<MarketEditorCheckListController
							mode='edit'
							value={value}
							onChanged={onChange}
							setBusy={this.props.setBusy}
						/>
					)
				},
				render: (text: any, record: QuoteSummary) => {
					return record.marketName;
				},
			},
			{
				key: 'brandName',
				title: 'Brand',
				dataIndex: 'brandId',
				sortable: true,
				filter: {
					field: 'brandId',
					type: 'foreignKey',
					render: (value, onChange) => (
						<BrandEditorCheckListController
							mode='edit'
							value={value}
							onChanged={onChange}
							setBusy={this.props.setBusy}
						/>
					)
				},
				render: (text: any, record: QuoteSummary) => {
					return record.brandName;
				},
			},
			{
				key: 'agentName',
				title: 'Agent',
				dataIndex: 'agentId',
				sortable: true,
				render: (text: any, record: QuoteSummary) => {
					return record.agentName;
				},
			},
			{
				key: 'totalSaleGross',
				title: 'Sell price',
				dataIndex: 'totalSaleGross',
				render: (text, record: QuoteSummary) => {
					return `${text} (${record.marketCurrencyIso3Name})`;
				}
			},
			{
				key: 'balance',
				title: 'Balance',
				dataIndex: 'balance',
				render: (text, record: QuoteSummary) => {
					return `${ObjectUtils.round(record.totalSaleGross - record.paid)} (${record.marketCurrencyIso3Name})`;
				}
			},
			{
				key: 'managedById',
				title: 'Salesman',
				dataIndex: 'managedById',
				render: (text, record: QuoteSummary) => {
					return record.managedByUserName;
				},
				sortable: true,
				filter: {
					field: 'managedById',
					type: 'foreignKey',
					render: (value, onChange) => (
						<UserEditorController
							label={null}
							isRole={false}
							mode='edit'
							value={value}
							onChanged={(value: UserSummary) => { onChange(value ? value.id : null) }}
							setBusy={this.props.setBusy}
						/>
					)
				}
			},
			{
				key: 'dateFrom',
				title: 'From',
				dataIndex: 'dateFrom',
				sortable: true,
				render: (text, record: QuoteSummary) => {
					return record.dateFromText;
				},
				filter: {
					field: 'dateFrom',
					type: 'date',
					render: (value, onChange) => (
						<DateView
							mode="edit"
							label={null}
							value={value}
							onChanged={(newValue) => {
								if (newValue) {
									onChange(newValue.toISOString());
								} else {
									onChange(null);
								}
							}}
							format="small-date"
						/>
					)
				}
			},
			{
				key: 'dateTo',
				title: 'To',
				dataIndex: 'dateTo',
				sortable: true,
				render: (text, record: QuoteSummary) => {
					return record.dateToText;
				},
				filter: {
					field: 'dateTo',
					type: 'date',
					render: (value, onChange) => (
						<DateView
							mode="edit"
							label={null}
							value={value}
							onChanged={(newValue) => {
								if (newValue) {
									onChange(newValue.toISOString());
								} else {
									onChange(null);
								}
							}}
							format="small-date"
						/>
					)
				}
			},
			{
				key: 'reservationDate',
				title: 'Reservation Date',
				dataIndex: 'reservationDate',
				render: (text, record: QuoteSummary) => {
					return record.reservationDateText;
				},
				sortable: true,
				filter: {
					field: 'reservationDate',
					type: 'date',
					render: (value, onChange) => (
						<DateView
							mode="edit"
							label={null}
							value={value}
							onChanged={(newValue) => {
								if (newValue) {
									onChange(newValue.toISOString());
								} else {
									onChange(null);
								}
							}}
							format="small-date"
						/>
					)
				}
			}
		];

		if (hiddenColumns) {
			for (const columnName of hiddenColumns) {
				const index = columns.findIndex(m => m.dataIndex === columnName);
				if (index > -1) {
					columns.splice(index, 1);
				}
			}
		}

		const isReadonly = this.props.readonly || !this.permissions.some((r) => this.identityService.isAuthorized(r));

		return (
			<DataTableView
				setBusy={this.props.setBusy}
				applyParamsFilter={['reservationDate', 'dateTo', 'dateFrom']}
				hideToolbar={isReadonly ? ToolbarVisibility.hideModifyButtons : null}
				columns={columns}
				model={model}
				itemDoubleClick={(item) => {
					if (this.props.onItemDoubleClicked) {
						this.props.onItemDoubleClicked(item);
					}
				}}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/list/quote-list.view.tsx